import * as React from 'react';
import { connect } from 'react-redux';
import withStyles from 'react-jss';
import { Modal, Radio, Form, Button, Select, Icon, Tag, message, InputNumber } from 'antd';

import styles from './styles';
import { updateReferenceRate, fetchLowestBid } from 'src/api/inquiry';
import { InquiryModes } from 'src/Library/Constants';
import { tokens } from 'src/design-system';

const FormItem = Form.Item;
const { Option } = Select;

const referenceRatePerUnit = {
  FREIGHT: 'freightPerUnit',
  TOTAL: 'totalPerUnit',
};

function EditReferenceRateModal(props) {
  const {
    classes,
    isModalActive,
    onModalClose,
    form,
    inquiryMaster,
    masterData,
    referenceRateData,
    inquiryId,
    refreshInquiryData,
    mode,
  } = props;
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isUpdating, setIsUpdating] = React.useState<boolean>(false);
  const [bidOption, setBidOption] = React.useState<number>(0);
  const [lowestBidData, setLowestBidData] = React.useState<any>({});
  const [isFormValid, setIsFormValid] = React.useState<boolean>(false);
  const { getFieldDecorator, getFieldValue, resetFields, setFieldsValue, validateFields } =  form;

  const referenceRateBasisKey = referenceRateData &&
    referenceRateData.basis &&
    referenceRateData.basis.toUpperCase();

  const parseFloatToTwo = (num) => {
    return (!!num ? Number(num).toFixed(2) : '');
  };

  const handleCancel = () => {
    onModalClose();
  };

  const handleSubmit = async () => {
    validateFields([(mode === InquiryModes.TRAILER || mode === InquiryModes.DOMESTIC) ? '' : 'total_price',
    'currency', 'price_per_unit'], async (errors, values) => {
      if (!errors) {
        setIsUpdating(true);
        const payload = {
          inquiryId,
          referenceRate: {
            basis: mode === InquiryModes.TRAILER ? 'FREIGHT': (mode === InquiryModes.DOMESTIC ? 'TOTAL' : getFieldValue('total_price')),
            currency: getFieldValue('currency'),
            amount: parseFloatToTwo(getFieldValue('price_per_unit')),
          },
        };
        const response = await updateReferenceRate(payload);
        if (response.isSuccess) {
          message.success('reference rate successfully updated!');
          refreshInquiryData();
          handleCancel();
        } else {
          message.error(response.errorMessage);
        }
        setIsUpdating(false);
      } else {
        message.warn('invalid form values');
        setIsFormValid(false);
      }
    });
  };

  const getAmountFromBasis = (basis) => {
    const referenceRatePerUnitKey = referenceRatePerUnit[basis];
    return lowestBidData.data.data[0][referenceRatePerUnitKey].amount;
  };

  const getCurrencyFromBasis = (basis) => {
    const referenceRatePerUnitKey = referenceRatePerUnit[basis];
    return lowestBidData.data.data[0][referenceRatePerUnitKey].currency;
  };

  const handleRadioChange = async (e) => {
    if (e.target.value) {
      setIsLoading(true);
      const response = await fetchLowestBid({ inquiryId });
      if (response.isSuccess) {
        if (Array.isArray(response.data.data) && response.data.data.length > 0) {
          setIsFormValid(true);
          setLowestBidData(response);
          setIsLoading(false);
          setBidOption(e.target.value);
          setIsLoading(false);
          const basis = getFieldValue('total_price');
          const amount = (basis === 'FREIGHT')
          ? parseFloatToTwo(response.data.data[0].freightPerUnit.amount)
          : parseFloatToTwo(response.data.data[0].totalPerUnit.amount);
          const currency = (basis === 'FREIGHT')
          ? response.data.data[0].freightPerUnit.currency
          : response.data.data[0].totalPerUnit.currency;
          setFieldsValue({
            currency,
            price_per_unit: amount,
          });
        } else {
          message.warn('no values found');
          setBidOption(0);
          resetFields(['previous_rates']);
          setIsLoading(false);
        }
      } else {
        message.error(response.errorMessage);
        setBidOption(0);
        resetFields(['previous_rates']);
        setIsLoading(false);
      }
    } else {
      setBidOption(e.target.value);
    }
  };

  const handleBasisChange = (basis) => {
    if (bidOption === 1) {
      const amount = parseFloatToTwo(getAmountFromBasis(basis));
      const currency = getCurrencyFromBasis(basis);
      setFieldsValue({
        currency,
        price_per_unit: amount,
      });
    }
  };

  const validate = (val) => {
    if (!!val) {
      setIsFormValid(true);
    } else {
      setIsFormValid(false);
    }
  };

  React.useEffect(() => {
    validateFields([(mode === InquiryModes.TRAILER || mode === InquiryModes.DOMESTIC) ? '' : 'total_price',
    'currency', 'price_per_unit'], async (errors, values) => {
      const { total_price, currency, price_per_unit } = values;
      if (((mode === InquiryModes.TRAILER || mode === InquiryModes.DOMESTIC ) ? true : total_price ) && currency && price_per_unit) {
        setIsFormValid(true);
      } else {
        setIsFormValid(false);
      }
    });
  }, []);

  return (
    <Modal
      title="Revise Reference Rate"
      visible={ isModalActive }
      onCancel={ handleCancel }
      className= { classes.editReferenceRateModal }
      okText="Submit"
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={ handleCancel }>
          Cancel
        </Button>,
        <Button
          key="submit"
          className={ classes.okButton }
          onClick={ handleSubmit }
          loading={ isUpdating }
          disabled={ !isFormValid }>
          Submit
        </Button>,
      ]}>
      <Form layout="vertical">
        <div className={ classes.selectContainer }>
          <span className={ classes.selectlabel }> <b>Reference Rate</b> </span>
          <span>
            {
              isLoading && <Icon type="loading" style={{ color: 'rgb(24, 144, 255)', fontSize: `${tokens.fontSize.h2}px` }}>
              </Icon>
            }
          </span>
          {(mode !== InquiryModes.TRAILER && mode !== InquiryModes.DOMESTIC) && <FormItem validateStatus="validating">
          {
            getFieldDecorator('total_price', {
              rules: [{ required: true, message: 'Please input total price!' }],
              initialValue: referenceRateBasisKey || 'FREIGHT',
            })(
              <Select
                placeholder="Total Price"
                className={ classes.rateSelect }
                onSelect={ handleBasisChange }
                >
                { Object.keys(inquiryMaster.referenceRateBasis).map((key) => {
                  return <Option key={ key }>{ inquiryMaster.referenceRateBasis[key] }</Option>;
                }) }
              </Select>,
            )
          }
          </FormItem>}
          <FormItem>
          {
            getFieldDecorator('currency', {
              rules: [{ required: true, message: 'Please input currency!' }],
              initialValue: referenceRateData && referenceRateData.currency,
            })(
              <Select
                placeholder="Currency"
                className={ classes.rateSelect }
                disabled={ bidOption === 1}
                onSelect={ validate }
                >
                {
                  Object.keys(masterData.currencySymbols).map((key) => {
                    return <Option
                      key={ key }>{ key }</Option>;
                  })
                }
              </Select>,
            )
          }
          </FormItem>
          <FormItem>
          {
            getFieldDecorator('price_per_unit', {
              rules: [{ required: true, message: 'Please input price per unit!' }],
              initialValue: referenceRateData && parseFloatToTwo(referenceRateData.amount),
              validateTrigger: 'onChange',
            })(
              <InputNumber
                placeholder="Price Per Unit"
                className={ classes.rateSelect }
                disabled={ bidOption === 1 }
                onChange={ validate }
                >
              </InputNumber>,
            )
          }
          </FormItem>
          {mode === InquiryModes.TRAILER &&
            <span className={classes.perTrailer}>
              Per Trailer
            </span>
          }
          {mode === InquiryModes.DOMESTIC &&
            <span className={classes.perTrailer}>
              Per Vehicle
            </span>
          }
        </div>

        <div>Would you like to use any of the previously used Reference Rate?</div> <br/>

        <FormItem>
          {
            getFieldDecorator('previous_rates', {
              rules: [{ required: true, message: 'This field cannot be left empty!' }],
              initialValue: 0,
            })(
              <Radio.Group onChange ={ handleRadioChange }>
                <Radio className={ classes.referenceRateRadio } value={ 1 } disabled={ isLoading }>
                  Lowest Bid of Current Inquiry <Tag color="geekblue">Rank 1</Tag>
                </Radio>
                <Radio className={ classes.referenceRateRadio } value={ 0 } disabled={ isLoading }>
                  None
                </Radio>
              </Radio.Group>,
            )
          }
        </FormItem>
      </Form>
    </Modal>
  );
}

const mapStateToProps = ({ inquiryReducer, masterData }, ownProps) => ({
  masterData,
  inquiryMaster: inquiryReducer.masterData,
});

const styledEditReferenceRateModal = withStyles(styles)(Form.create()(EditReferenceRateModal));
export default connect(mapStateToProps)(styledEditReferenceRateModal);
